import { defineStore } from 'pinia';
import { deleteCompanyImage, fetchCompanies, fetchIDPs, updateCompany, updateCompanyImage } from '@/api/companies';
import { loadLocaleMessages } from '@/plugins/i18n';
import type { Company, Idp } from '@/types';

const FALLBACK_CURRENCY_CODE = 'USD';

export const useCompanyStore = defineStore('company', {
  state: () => ({
    company: {} as Company,
    idps: [] as Idp[],
  }),
  actions: {
    async fetchCompanies() {
      const { data } = await fetchCompanies();

      if (data && data.length) {
        this.company = data[0];
      }
    },
    async fetchIDPs() {
      const { data } = await fetchIDPs();

      this.idps = data ?? [];
    },
    async updateCompanyImage(image: File) {
      const { error } = await updateCompanyImage(this.company.id, image);

      if (!error) {
        await this.fetchCompanies();
      }

      return { error };
    },
    async deleteCompanyImage() {
      const { error } = await deleteCompanyImage(this.company.id);

      if (!error) {
        await this.fetchCompanies();
      }

      return { error };
    },
    async updateCompany(payload: Partial<Company>) {
      const { error } = await updateCompany(this.company.id, payload);

      if (!error) {
        payload.locale && loadLocaleMessages(payload.locale);
        await this.fetchCompanies();
      }

      return { error };
    },
  },
  getters: {
    showPeoplePage: (state) => state.company?.show_people_page,
    hasCompCyclePlanning: (state) => state.company?.has_comp_cycle_planning,
    compCycleAdminImportsEnabled: (state) => state.company?.comp_cycle_admin_imports_enabled,
    hasPbeConfig: (state) => state.company?.has_pbe_config,
    companyName: (state) => state.company?.name,
    freePage: (state) => state.company?.free_page,
    isUsingGreenhouseV3: (state) => state.company?.using_greenhouse_v3,
    isUsingGreenhouse: (state) => state.company?.using_greenhouse,
    isUsingMerge: (state) => state.company?.using_merge,
    isUsingSSO: (state) => state.company?.using_sso,
    isUsingOIDC: (state) => state.company?.using_oidc,
    isUsingMFA: (state) => state.company?.using_mfa,
    hasAnalytics: (state) => state.company?.has_analytics,
    isFreeTier: (state) => state.company?.is_free_tier,
    companyUploadedLogoImage: (state) => state.company?.logo_image,
    companyLogoUrl: (state) => {
      return state.company?.logo_image || state.company?.logo_url;
    },
    isUsingApprovals: (state) => state.company?.using_approvals,
    isUsingMarketPulse: (state) => state.company?.has_market_pulse,
    isBonusPercentageFormat(state) {
      const targetIncentivePayTypes = { currency: 0, percentage: 1 };
      return state.company?.target_incentive_pay_type === targetIncentivePayTypes.percentage;
    },
    usingCrypto: (state) => state.company?.using_crypto,
    showRangesHourlyRates: (state) => state.company?.show_ranges_hourly_rates,
    totalEquityGrossType: (state) => state.company?.total_equity_gross_type,
    usingEquityV2: (state) => state.company?.using_equity_v2,
    usingEmployeePortal: (state) => state.company?.using_employee_portal,
    nonAdminAccessToEmployeePortal: (state) => {
      return state.company?.employee_portal_settings?.non_admin_access;
    },
    hasTeamleadsGroup: (state) => state.company?.has_teamleads_group,
    usingRangeSpanWidget: (state) => state.company?.using_range_span_widget,
    isAdpTenant: (state) => state.company?.is_adp_tenant ?? false,
    hrisProviderName: (state) => state.company?.hris_provider_name ?? '',
    defaultCurrencyCode: (state) => state.company?.default_currency_code || FALLBACK_CURRENCY_CODE,
  },
});
